<template>
  <v-card :class="isMobile ? 'table-card pl-2 pr-2' : 'table-card'" elevation="0">
    <v-card-title class="pl-0 pt-0">
      {{ $t('queues.reports.callDetails.title') }}
    </v-card-title>
    <v-card-subtitle class="pl-0 pb-0">
      <v-icon>mdi-calendar</v-icon>&nbsp;
      <span v-if="scope === 'day'"><strong>{{ startDate.substring(0, 10) }}</strong></span>
      <span v-else-if="scope === 'hour'" v-html="$t('queues.reports.scopeDisplays.hour',
        { date: startDate.substring(0, 10), start: startDate.substring(11, 16), end: endDate.substring(11, 16) })" />
      <span v-else v-html="$t('queues.reports.scopeDisplays.range', { start: startDate, end: endDate })"></span>
    </v-card-subtitle>
    <v-card-subtitle class="pl-0 pt-0">
      <v-icon>mdi-human-queue</v-icon>&nbsp;
      {{ selectedQueues.join(', ') }}
    </v-card-subtitle>
    <v-data-table
      :headers="headers"
      :items="reportRows.result"
      :options.sync="pagination"
      :server-items-length="pagination.totalItems"
      :loading="loading"
      :disable-sort="true"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      class="report-table"
      >
      <template v-slot:[`item.date_time`]="{ item }">
        {{ formatDate(item.date_time) }}
      </template>
      <template v-slot:[`item.callerId`]="{ item }">
        {{ formatClid(item.callerId) }}
      </template>
      <template v-slot:item.status="{ item }">
        {{ $t(`queues.reports.callDetails.statuses.${item.status}`) ?
          $t(`queues.reports.callDetails.statuses.${item.status}`) :
          $t('queues.reports.callDetails.statuses.notMapped') }}
      </template>
      <template v-slot:item.agents_display="{ item }">
        {{ item.agents_display ? item.agents_display.map((agent) => formatAgent(agent)).join(', ') : '' }}
      </template>
      <template v-slot:item.holdtime="{ item }">
        {{ hourTemplate(item.holdtime) }}
      </template>
      <template v-slot:item.callDuration="{ item }">
        <span v-if="!item.callDuration"><v-icon>mdi-phone-remove</v-icon></span>
        <span v-else>{{ hourTemplate(item.callDuration) }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn v-if="item.recording && item.cdr_id" icon color="primary" @click="$emit('playRecording', item.cdr_id)">
          <v-icon>mdi-play</v-icon>
        </v-btn>
        <v-btn icon color="primary" @click="$refs.callDialog.loadCallDetails(item.callId)">
          <v-icon>mdi-information-outline</v-icon>
        </v-btn>
      </template>
      <template slot="body.append">
        <tr v-if="$vuetify.breakpoint.mobile" class="v-data-table__mobile-table-row">
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.callDetails.table.total') }}</div>
            <div class="v-data-table__mobile-row__cell"></div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.callDetails.table.holdTime') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(reportRows.totalHold) }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.callDetails.table.callDuration') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(reportRows.totalTalk) }}</div>
          </td>
        </tr>
        <tr v-else style="font-weight:bold">
          <td colspan="5">{{ $t('queues.reports.callDetails.table.total') }}</td>
          <td class="text-center">{{ hourTemplate(reportRows.totalHold) }}</td>
          <td class="text-center">{{ hourTemplate(reportRows.totalTalk) }}</td>
          <td></td>
        </tr>
      </template>
    </v-data-table>
    <CallDetailsDialog :scope="scope" :timezone="timezone" ref="callDialog" />
  </v-card>
</template>

<script>
import formatPhoneNum from '@/filters/phone_num'
import { formatDateReduced } from '@/filters/format_date'
import formatTime from '@/filters/format_time'
import formatClid from '@/filters/format_clid'
import CallDetailsDialog from './call_details_dialog'

export default {
  name: 'queues-reports-call-details',
  components: { CallDetailsDialog },
  props: {
    reportRows: {
      type: Object,
      required: true
    },
    selectedQueues: {
      type: Array,
      default: () => []
    },
    scope: {
      type: String,
      default: 'month'
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    },
    timezone: {
      type: String,
      default: 'UTC'
    }
  },
  data () {
    return {
      loading: false,
      isWatcherActive: false,
      pagination: {
        page: 1,
        itemsPerPage: 25,
        totalItems: 0
      }
    }
  },
  mounted () {
    this.pagination.totalItems = this.reportRows.totalRecords || 0
    this.$nextTick(() => {
      this.isWatcherActive = true
    })
  },
  watch: {
    pagination: {
      handler () {
        if (this.isWatcherActive) this.load()
      },
      deep: true
    },
    reportRows: {
      immediate: true,
      handler (newVal) {
        this.pagination.totalItems = newVal.totalRecords || 0
      }
    }
  },
  computed: {
    isMobile () {
      return this.$vuetify.breakpoint.mobile
    },
    headers () {
      return [
        { text: this.$t('queues.reports.callDetails.table.dateTime'), value: 'date_time', width: 150 },
        { text: this.$t('queues.reports.callDetails.table.callerId'), value: 'callerId' },
        { text: this.$t('queues.reports.callDetails.table.callStatus'), value: 'status' },
        { text: this.$t('queues.reports.callDetails.table.agents'), value: 'agents_display' },
        { text: this.$t('queues.reports.callDetails.table.queue'), value: 'queue' },
        { text: this.$t('queues.reports.callDetails.table.holdTime'), value: 'holdtime', align: 'center', width: 120 },
        { text: this.$t('queues.reports.callDetails.table.callDuration'), value: 'callDuration', align: 'center', width: 120 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 105 }
      ]
    }
  },
  methods: {
    hourTemplate (seconds) {
      if (!seconds) return '00:00'
      return formatTime(seconds)
    },
    formatClid (clid) {
      if (!clid) {
        return ''
      } else if (clid.indexOf('"') !== -1) {
        return clid.replaceAll('"', '')
      } else {
        return formatPhoneNum(clid)
      }
    },
    formatDate (date) {
      if (this.$i18n.locale === 'en-US') return formatDateReduced(date, this.scope, this.$i18n.locale, false, true)
      return formatDateReduced(date, this.scope, this.$i18n.locale, false)
    },
    formatAgent (agent) {
      if (agent === 'NONE') return this.$t('queues.reports.none')
      return formatClid(agent)
    },
    load () {
      this.loading = true
      try {
        this.$emit('refreshData', {
          reportType: 'callDetails',
          queue: this.selectedQueues,
          startDate: this.startDate,
          endDate: this.endDate,
          limit: this.pagination.itemsPerPage,
          offset: (this.pagination.page - 1) * this.pagination.itemsPerPage
        })
      } catch (e) {
        this.dbg(e.response)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
